import { useCallback, useEffect, useRef, useState } from 'react';
import { dashboardsService } from '@/services/dashboards.service';
import type { ApontamentosResponse, FiltroApontamentos } from '@/types/dashboards';
import { errosDaApi } from '@/utils/equiplexApontamentos';

export interface EstadoApontamentos {
  resposta: ApontamentosResponse | null;
  carregando: boolean;
  erro: string | null;
  carregar: () => Promise<void>;
}

/**
 * Lista de apontamentos do filtro (o dos painéis + máquina). Troca rápida de filtro não deixa
 * resposta velha por cima da nova: só a última requisição grava. `habilitado = false` segura a
 * carga (o filtro ainda depende do retrato).
 */
export function useApontamentos(filtro: FiltroApontamentos, habilitado = true): EstadoApontamentos {
  // Uma chave estável: o objeto do filtro é recriado a cada render de quem chama.
  const chave = JSON.stringify(filtro);
  const [resposta, setResposta] = useState<ApontamentosResponse | null>(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const ultima = useRef(0);

  const carregar = useCallback(async () => {
    const minha = ++ultima.current;
    setCarregando(true);
    try {
      const dados = await dashboardsService.listarApontamentos(JSON.parse(chave) as FiltroApontamentos);
      if (minha !== ultima.current) return;
      setResposta(dados);
      setErro(null);
    } catch (e) {
      if (minha !== ultima.current) return;
      setErro(errosDaApi(e, 'Não foi possível carregar os apontamentos.').geral);
    } finally {
      if (minha === ultima.current) setCarregando(false);
    }
  }, [chave]);

  useEffect(() => {
    if (habilitado) carregar();
  }, [carregar, habilitado]);

  return { resposta, carregando, erro, carregar };
}
